/**
 * A brand's two colours → the shopper pages' `--v-*` tokens (#462).
 *
 * The cutting mat is laid in the brand's primary: as given on the dark mat
 * when it is dark enough, a pale tint of it on the light mat with the title
 * written in it. The accent is the main button, links and marks.
 *
 * Every pair of text and background is held to WCAG AA (4.5:1 for text, 3:1
 * for icons and marks, 7:1 for body text). A colour that falls short keeps its
 * hue and only gets lighter or darker, by the smallest step that reaches the
 * ratio, so a pale or near-black brand colour can't make the page unreadable.
 * The trust marks (`--v-trust`) are never touched: they stay BasiQR's.
 */

export type ShopperTheme = 'light' | 'dark';

type Rgb = [number, number, number];
interface Lch {
  L: number;
  C: number;
  h: number;
}

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));

// ── sRGB ↔ OKLab / OKLCH ──────────────────────────────────────────────
const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
const fromLinear = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);

const HEX = /^#([0-9a-f]{6})$/i;

/** True for `#rrggbb` (any case). */
export const isHexColor = (value: unknown): value is string => typeof value === 'string' && HEX.test(value);

function parseHex(hex: string): Rgb {
  const n = parseInt(HEX.exec(hex)![1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

const toHex = (rgb: Rgb) =>
  '#' + rgb.map((v) => Math.round(clamp(v, 0, 1) * 255).toString(16).padStart(2, '0')).join('');

function rgbToLch([r, g, b]: Rgb): Lch {
  const R = toLinear(r);
  const G = toLinear(g);
  const B = toLinear(b);
  const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B);
  const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B);
  const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const C = Math.hypot(a, bb);
  const h = C < 1e-4 ? 0 : (Math.atan2(bb, a) * 180) / Math.PI;
  return { L, C, h: (h + 360) % 360 };
}

function lchToRgbRaw({ L, C, h }: Lch): Rgb {
  const hr = (h * Math.PI) / 180;
  const a = C * Math.cos(hr);
  const b = C * Math.sin(hr);
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
  const s = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3);
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ].map(fromLinear) as Rgb;
}

const inGamut = (rgb: Rgb) => rgb.every((v) => v >= -1e-4 && v <= 1 + 1e-4);

/** Keep lightness and hue; lower chroma until the colour fits in sRGB. */
function lchToRgb(c: Lch): Rgb {
  const L = clamp(c.L, 0, 1);
  const direct = lchToRgbRaw({ ...c, L });
  if (inGamut(direct)) return direct.map((v) => clamp(v, 0, 1)) as Rgb;
  let lo = 0;
  let hi = c.C;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (inGamut(lchToRgbRaw({ L, C: mid, h: c.h }))) lo = mid;
    else hi = mid;
  }
  return lchToRgbRaw({ L, C: lo, h: c.h }).map((v) => clamp(v, 0, 1)) as Rgb;
}

const hexOf = (c: Lch) => toHex(lchToRgb(c));
const lchOf = (hex: string) => rgbToLch(parseHex(hex));

// ── WCAG 2 contrast ───────────────────────────────────────────────────
function luminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map(toLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2 contrast ratio of two `#rrggbb` colours (1 to 21). */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

const isLight = (hex: string) => luminance(hex) > 0.179;

/**
 * The colour, moved the least in lightness (hue and chroma kept, within sRGB)
 * to reach `ratio` against `bg`: darker on a light background, lighter on a
 * dark one. Black or white when the hue can't get there.
 */
function fit(c: Lch, bg: string, ratio: number): string {
  const start = hexOf(c);
  if (contrast(start, bg) >= ratio) return start;
  const darker = isLight(bg);
  let lo = darker ? 0 : c.L;
  let hi = darker ? c.L : 1;
  for (let i = 0; i < 30; i++) {
    const mid = (lo + hi) / 2;
    const ok = contrast(hexOf({ ...c, L: mid }), bg) >= ratio;
    if (darker === ok) lo = mid;
    else hi = mid;
  }
  const out = hexOf({ ...c, L: darker ? lo : hi });
  return contrast(out, bg) >= ratio ? out : darker ? '#000000' : '#ffffff';
}

function rgba(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex).map((v) => Math.round(v * 255));
  return `rgb(${r} ${g} ${b} / ${alpha})`;
}

export interface ButtonColours {
  fill: string;
  ink: string;
  /** The fill isn't the brand's accent as given. */
  moved: boolean;
}

/**
 * The main button: the accent as given when its label reads on it, the label
 * white or ink, whichever reads better. A fill the same tone as the page is
 * first lifted to 3:1 against it, or the button would vanish.
 */
function button(accentHex: string, inkDark: string, bg: string): ButtonColours {
  const given = accentHex.toLowerCase();
  let accent = lchOf(given);
  let fill = hexOf(accent);
  if (contrast(fill, bg) < 1.25) {
    fill = fit(accent, bg, 3);
    accent = lchOf(fill);
  }
  const best = contrast(fill, '#ffffff') >= contrast(fill, inkDark) ? '#ffffff' : inkDark;
  if (contrast(fill, best) >= 4.5) return { fill, ink: best, moved: fill !== given };
  return { fill: fit(accent, best, 4.5), ink: best, moved: true };
}

export interface ContrastCheck {
  /** What the pair is, in words for Settings. */
  name: string;
  fg: string;
  bg: string;
  ratio: number;
  /** The ratio it must reach. */
  need: number;
}

export interface BrandTheme {
  theme: ShopperTheme;
  /** CSS custom properties for `.vault[data-vault-theme=…]`. */
  tokens: Record<string, string>;
  /** The mat's base colour: body background and `<meta name="theme-color">`. */
  background: string;
  button: ButtonColours;
  checks: ContrastCheck[];
}

/**
 * The tokens for one theme. `primary` and `accent` are `#rrggbb`; the result
 * holds every text/background pair at AA or better (see `checks`).
 */
export function brandTheme(primary: string, accent: string, theme: ShopperTheme): BrandTheme {
  const P = lchOf(primary);
  const A = lchOf(accent);
  const t: Record<string, string> = {};
  const checks: ContrastCheck[] = [];
  const check = (name: string, fg: string, bg: string, need: number) =>
    checks.push({ name, fg, bg, need, ratio: contrast(fg, bg) });

  if (theme === 'dark') {
    // A dark primary is the mat as given; a lighter one gives a deep shade of
    // its hue, and a pale one a quiet one (deep yellows and pinks turn muddy).
    const bgL = clamp(P.L, 0.24, 0.36);
    const bgC = Math.min(P.C, P.L > 0.8 ? 0.045 : 0.12);
    const bg = hexOf({ L: bgL, C: bgC, h: P.h });
    t['--v-bg'] = bg;
    t['--v-mat-base'] = `linear-gradient(160deg, ${hexOf({ L: bgL + 0.03, C: bgC, h: P.h })} 0%, ${bg} 45%, ${hexOf({ L: bgL - 0.03, C: bgC, h: P.h })} 100%)`;
    const text = fit({ L: 0.965, C: Math.min(P.C, 0.012), h: P.h }, bg, 7);
    const text2 = fit({ L: 0.84, C: Math.min(P.C, 0.02), h: P.h }, bg, 4.5);
    const text3 = fit({ L: 0.72, C: Math.min(P.C, 0.025), h: P.h }, bg, 4.5);
    t['--v-text'] = text;
    t['--v-text-2'] = text2;
    t['--v-text-3'] = text3;
    t['--v-head'] = text;
    t['--v-sheet'] = rgba(hexOf({ L: bgL - 0.045, C: bgC, h: P.h }), 0.97);
    t['--v-seam'] = rgba(text, 0.28);
    t['--v-seg-thumb'] = text;
    t['--v-seg-ink'] = bg;
    const mark = fit(A, bg, 3);
    const link = fit(A, bg, 4.5);
    t['--v-accent'] = mark;
    t['--v-accent-ink'] = link;
    t['--v-mark'] = mark;
    t['--v-accent-wash'] = rgba(mark, 0.14);
    const btn = button(accent, hexOf({ L: 0.22, C: Math.min(P.C, 0.04), h: P.h }), bg);
    t['--v-cta'] = btn.fill;
    t['--v-cta-ink'] = btn.ink;
    check('Body text', text, bg, 7);
    check('Secondary text', text2, bg, 4.5);
    check('Small labels', text3, bg, 4.5);
    check('Links', link, bg, 4.5);
    check('Icons and marks', mark, bg, 3);
    check('Button label', btn.ink, btn.fill, 4.5);
    return { theme, tokens: t, background: bg, button: btn, checks };
  }

  const bg = hexOf({ L: 0.975, C: Math.min(P.C, 0.016), h: P.h });
  t['--v-bg'] = bg;
  t['--v-mat-base'] = bg;
  const text = fit({ L: 0.27, C: Math.min(P.C, 0.03), h: P.h }, bg, 7);
  const text2 = fit({ L: 0.45, C: Math.min(P.C, 0.04), h: P.h }, bg, 4.5);
  const text3 = fit({ L: 0.56, C: Math.min(P.C, 0.04), h: P.h }, bg, 4.5);
  const head = fit(P, bg, 4.5);
  t['--v-text'] = text;
  t['--v-text-2'] = text2;
  t['--v-text-3'] = text3;
  t['--v-head'] = head;
  t['--v-grid-minor'] = rgba(text, 0.04);
  t['--v-grid-major'] = rgba(text, 0.075);
  t['--v-edge'] = rgba(text, 0.12);
  t['--v-edge-2'] = rgba(text, 0.2);
  t['--v-seam'] = rgba(text, 0.32);
  t['--v-sheet'] = rgba(hexOf({ L: 0.99, C: Math.min(P.C, 0.008), h: P.h }), 0.98);
  t['--v-seg-ink'] = text;
  const mark = fit(A, bg, 3);
  const link = fit(A, bg, 4.5);
  t['--v-accent'] = mark;
  t['--v-accent-ink'] = link;
  t['--v-mark'] = mark;
  t['--v-accent-wash'] = rgba(mark, 0.1);
  const btn = button(accent, text, bg);
  t['--v-cta'] = btn.fill;
  t['--v-cta-ink'] = btn.ink;
  t['--v-cta-shadow'] = `0 14px 28px -14px ${rgba(btn.fill, 0.7)}`;
  check('Body text', text, bg, 7);
  check('Secondary text', text2, bg, 4.5);
  check('Small labels', text3, bg, 4.5);
  check('Title', head, bg, 4.5);
  check('Links', link, bg, 4.5);
  check('Icons and marks', mark, bg, 3);
  check('Button label', btn.ink, btn.fill, 4.5);
  return { theme, tokens: t, background: bg, button: btn, checks };
}

/**
 * The stylesheet that puts a brand's colours on every shopper surface of the
 * page, sheets included (they render outside the page's root). Specificity
 * beats vault.css's theme blocks. Null when the brand has no colours.
 */
export function brandThemeCss(primary?: string, accent?: string): string | null {
  if (!isHexColor(primary) || !isHexColor(accent)) return null;
  return (['dark', 'light'] as const)
    .map((theme) => {
      const { tokens } = brandTheme(primary, accent, theme);
      const body = Object.entries(tokens)
        .map(([name, value]) => `${name}:${value}`)
        .join(';');
      return `.vault.vault[data-vault-theme="${theme}"]{${body}}`;
    })
    .join('\n');
}
